import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePermission } from '../../core/utils/PermissionContext';
import { all_routes } from '../../router/all_routes';
import { ACCESS_KEYS } from './access-utils';
import RolesTab from './roles-tab';
import RoleMatrixTab from './role-matrix-tab';
import CatalogTab from './catalog-tab';
import PreviewTab from './preview-tab';
import AuditTab from './audit-tab';
import './access-control.css';

/**
 * Access Control (RBAC) module. Each screen is a submodule page with its own route, menu entry and
 * permission keys (RBAC.<PAGE>.VIEW/CREATE/EDIT); the route guard checks VIEW, the API checks every call.
 */
const AccessPageShell = ({ title, subtitle, children }) => (
  <div className="page-wrapper">
    <div className="content access-control">
      <div className="page-header">
        <div className="add-item d-flex">
          <div className="page-title">
            <h4>{title}</h4>
            <h6>{subtitle}</h6>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-body">{children}</div>
      </div>
    </div>
  </div>
);

export const AccessRolesPage = () => {
  const { can } = usePermission();
  const navigate = useNavigate();
  const openMatrix = roleId => navigate(`${all_routes.accessmatrix}?roleId=${roleId}`);

  return (
    <AccessPageShell title="Roles" subtitle="Map Microsoft Entra app roles to this application">
      <RolesTab
        canCreate={can(ACCESS_KEYS.ROLES_CREATE)}
        canEdit={can(ACCESS_KEYS.ROLES_EDIT)}
        onOpenMatrix={can(ACCESS_KEYS.MATRIX_VIEW) ? openMatrix : null}
      />
    </AccessPageShell>
  );
};

export const AccessMatrixPage = () => {
  const { can } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const roleId = Number(searchParams.get('roleId')) || null;
  const setRoleId = id => setSearchParams(id ? { roleId: String(id) } : {}, { replace: true });

  return (
    <AccessPageShell title="Permission Matrix" subtitle="Page, CRUD and button permissions for one role">
      <RoleMatrixTab canEdit={can(ACCESS_KEYS.MATRIX_EDIT)} roleId={roleId} onRoleChange={setRoleId} />
    </AccessPageShell>
  );
};

export const AccessCatalogPage = () => {
  const { can } = usePermission();
  return (
    <AccessPageShell title="Modules & Pages" subtitle="Menu modules, submodule pages, routes and button permissions">
      <CatalogTab canCreate={can(ACCESS_KEYS.CATALOG_CREATE)} canEdit={can(ACCESS_KEYS.CATALOG_EDIT)} />
    </AccessPageShell>
  );
};

export const AccessPreviewPage = () => (
  <AccessPageShell title="Effective Access" subtitle="Combined access of users holding several roles">
    <PreviewTab />
  </AccessPageShell>
);

export const AccessAuditPage = () => (
  <AccessPageShell title="Audit Log" subtitle="Every change to roles, permissions and the catalog">
    <AuditTab />
  </AccessPageShell>
);
